import {Component, OnDestroy, OnInit} from "@angular/core";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {filter, finalize, map, takeUntil, withLatestFrom} from "rxjs/operators";
import {ActivatedRoute} from "@angular/router";
import {FormControl, FormGroup, ValidatorFn, Validators} from "@angular/forms";
import {Dialog} from "@angular/cdk-experimental/dialog";
import {DisclaimerComponent} from "../components/disclaimer.component";

interface FormData {
    password: string
    confirmPassword: string
}

@Component({
    selector: "app-reset",
    templateUrl: "./reset.component.html",
    styleUrls: ["./reset.component.scss"]
})
export class ResetComponent implements OnInit, OnDestroy {

    private readonly reset$ = new Subject<boolean>()
    public readonly submitting$ = new BehaviorSubject<boolean>(false)
    public readonly submitError$ = new BehaviorSubject<string | null>(null)
    private readonly destroyed$ = new Subject<boolean>()

    public readonly password = new FormControl(null, [Validators.required, Validators.minLength(8)])
    public readonly confirmPassword = new FormControl(null, [Validators.required, this.passwordsMatch(this.password)])
    private readonly token$: Observable<string>

    public readonly form = new FormGroup({
        password: this.password,
        confirmPassword: this.confirmPassword
    })

    constructor(route: ActivatedRoute,
                private readonly http: HttpClient,
                private readonly dialog: Dialog) {
        this.token$ = route.params
            .pipe(map((params) => params.token))
    }

    public ngOnInit() {
        this.reset$
            .pipe(takeUntil(this.destroyed$))
            .pipe(withLatestFrom(this.form.valueChanges, this.form.statusChanges, this.token$))
            .pipe(filter(([_, formData, status]) => status === "VALID"))
            .subscribe(([_, formData, status, token]) => this.sendSetPassword(formData, token))
    }

    public reset() {
        this.reset$.next(true)
    }

    public sendSetPassword(formData: FormData, token: string) {
        const data = {
            password: formData.password,
            token
        }

        this.submitting$.next(true)
        this.submitError$.next(null)

        this.http
            .post("/tradefloor/public_api/forgot/reset", data)
            .pipe(takeUntil(this.destroyed$))
            .pipe(finalize(() => this.submitting$.next(false)))
            .subscribe(() => this.redirectToApp(),
                (errorResponse) => this.resetError(errorResponse))

    }

    private redirectToApp() {
        window.location.assign("/tradefloor/#/")
    }

    private resetError(errorResponse: HttpErrorResponse) {
        this.submitError$.next(this.extractErrorMessage(errorResponse))
    }

    private extractErrorMessage(errorResponse: HttpErrorResponse) {
        switch (errorResponse.status) {
            case 404:
                return "Activation token has been used or has expired"
            case 503:
                return "We are performing maintenance. Please try again later."
            default:
                return "There was an error processing your request. Please try again later."
        }
    }

    private passwordsMatch(newPassword: FormControl): ValidatorFn {
        return (confirmControl) => {
            if (newPassword.value === confirmControl.value) {
                return null
            } else {
                return { "password-match": true }
            }
        }
    }

    public showTerms() {
        this.dialog.openFromComponent(DisclaimerComponent)
    }


    public ngOnDestroy(): void {
        this.destroyed$.next(true)
        this.destroyed$.complete()
    }
}
